import RafThrottle from "@utilities/raf-throttle";
import { tns } from 'tiny-slider/src/tiny-slider';
import Events from 'storefront/utilities/events';
import parseJSON from 'storefront/utilities/parse-json';
import setTabIndexOfChildren from 'storefront/utilities/set-tabindex-of-children';

const JS_HOOK_SLIDES = '[js-hook-slider-slides]';
const PAGE_DESIGNER_CONTAINER_CLASS = 'experience-region';
const JS_HOOK_SLIDER_CONTROLS = '[js-hook-slider-controls]';
const JS_HOOK_SLIDER_PREV = '[js-hook-slider-prev]';
const JS_HOOK_SLIDER_NEXT = '[js-hook-slider-next]';
const JS_HOOK_SLIDER_NAV = '[js-hook-slider-nav]';
const JS_HOOK_SLIDER_CURRENT = '[js-hook-slider-current]';
const JS_HOOK_SLIDER_TOTAL = '[js-hook-slider-total]';
const JS_HOOK_SLIDER_STATUS_BAR = '[js-hook-slider-status-bar]';
const JS_HOOK_SLIDER_INDICATOR = '[js-hook-slider-status-indicator]';
const JS_HOOK_SLIDER_META = '[js-hook-slider-meta]';

const TABINDEX = 'tabindex';

const HIDDEN_CLASS = 'u-hidden';

const DEFAULT_BREAKPOINTS = {
  1920: {
    items: 3,
    slideBy: 3,
    gutter: 16,
    disable: false,
  },
  1366: {
    items: 3,
    slideBy: 3,
    gutter: 16,
    disable: false,
  },
  1024: {
    items: 3,
    slideBy: 3,
    gutter: 16,
    disable: false,
  },
  768: {
    items: 3,
    slideBy: 3,
    gutter: 16,
    disable: false,
  },
  480: {
    items: 1.2,
    slideBy: 1,
    gutter: 8,
    disable: false,
  },
  320: {
    items: 1.2,
    slideBy: 1,
    gutter: 8,
    disable: false,
  },
  0: {
    disable: true,
  },
};

class Slider {
  constructor(element, config) {
    this.element = element;
    this.config = config;
    this.slider = this.getSlides();
    this.imagesData = this.getImagesData();
    this.numSlides = this.slider.children.length;
    this.instance = null;

    // Controls
    this.controls = this.element.querySelector(JS_HOOK_SLIDER_CONTROLS);
    this.prevButton = this.element.querySelector(JS_HOOK_SLIDER_PREV);
    this.nextButton = this.element.querySelector(JS_HOOK_SLIDER_NEXT);
    this.navElement = this.element.querySelector(JS_HOOK_SLIDER_NAV);
    this.currentLabel = this.element.querySelector(JS_HOOK_SLIDER_CURRENT);
    this.totalLabel = this.element.querySelector(JS_HOOK_SLIDER_TOTAL);
    this.sliderStatusBar = this.element.querySelector(JS_HOOK_SLIDER_STATUS_BAR);
    this.sliderStatusIndicator = this.element.querySelector(JS_HOOK_SLIDER_INDICATOR);
    this.sliderMeta = this.element.querySelector(JS_HOOK_SLIDER_META);
    this.showThumbs = this.element.dataset.showThumbs;

    if (this.totalLabel) this.totalLabel.innerHTML = this.numSlides;
  }

  get breakpoints() {
    if (this._breakpoints) return this._breakpoints;

    this._breakpoints = parseJSON(this.element.dataset.breakpoints, DEFAULT_BREAKPOINTS);

    this._breakpoints = this._breakpoints || DEFAULT_BREAKPOINTS;

    return this._breakpoints;
  }

  getSlides() {
    return this.element.querySelector(JS_HOOK_SLIDES).querySelector(`.${PAGE_DESIGNER_CONTAINER_CLASS}`)
      || this.element.querySelector(JS_HOOK_SLIDES)
      || this.element;
  }

  // Log image heights for use in comparison after image lazy loading
  getImagesData() {
    const images = this.slider.querySelectorAll('[js-hook-image] img');
    const imagesData = [];

    images.forEach(image => {
      imagesData.push({
        image,
        offsetHeight: image.offsetHeight
      });
    });

    return imagesData;
  }

  showControls() {
    if (this.controls) this.controls.classList.remove('u-hidden');
    if (this.prevButton) this.prevButton.removeAttribute(TABINDEX);
    if (this.nextButton) this.nextButton.removeAttribute(TABINDEX);
  }

  afterInit() {
    this.element.classList.add('is--initialised');
    Events.$trigger('lazyimage::update');
  }

  bindDefaultEvents() {
    RafThrottle.set([
      {
        element: window,
        event: 'resize',
        namespace: 'resizeSlider',
        fn: () => this.updateNavigation(),
        delay: 250,
      },
    ])

    this.instance.events.on('indexChanged', (data) => this.onIndexChanged(data));
  }

  onIndexChanged(data) {
    this.updateNavigation(data);
  }

  /**
   * Sets tab index of links in inactive slides to 0
   * @param slideItems {Array} | slide items array from tiny slider
   */
  setTabIndex(slideItems) {
    Array.from(slideItems).forEach((slide) => {
      if (slide.classList.contains('tns-slide-active')) {
        // link.tabIndex = 0;
        setTabIndexOfChildren(slide, 0);
      } else {
        // link.tabIndex = -1;
        setTabIndexOfChildren(slide, -1);
      }
    });
    return this;
  }

  updateNavigation(data) {
    const { displayIndex, items } = data || this.instance.getInfo();
    const intItems = parseInt(items, 10);

    this.updateNavigationLabel(displayIndex, intItems);
    this.updateSliderIndicator(displayIndex, intItems);
  }

  updateNavigationLabel(displayIndex, items) {
    if (!this.currentLabel) return;
    const totalShowing = this.numSlides < items ? this.numSlides : displayIndex + items - 1;
    this.currentLabel.innerHTML = items === 1 ? displayIndex : `${displayIndex} - ${totalShowing}`;
  }

  updateSliderIndicator(displayIndex, items) {
    if (!this.sliderStatusBar || !this.sliderStatusIndicator) return;

    const percentage = (displayIndex - 1) / (this.numSlides - items);
    const position = (this.sliderStatusBar.clientWidth - this.sliderStatusIndicator.clientWidth) * percentage;
    const width = items / this.numSlides * 100;

    if (width > 99) {
      this.sliderStatusBar.classList.add(HIDDEN_CLASS);
      if (this.sliderMeta) this.sliderMeta.classList.add(HIDDEN_CLASS);
    } else {
      this.sliderStatusBar.classList.remove(HIDDEN_CLASS);
      if (this.sliderMeta) this.sliderMeta.classList.remove(HIDDEN_CLASS);

      this.sliderStatusIndicator.style.width = `${width}%`;
      this.sliderStatusIndicator.style.transform = `translateX(${position}px)`;
    }
  }

  destroy() {
    if (typeof this.instance?.destroy === 'function') this.instance.destroy();
  }

  /**
   * On transition end event
   * @param data, data object returned by tiny slider
   */
  onTransitionEnd(data) {
    const { slideItems } = data;
    this.setTabIndex(slideItems);
    this.updateNavigation();
  }

  initSlider() {
    if (!this.numSlides || this.numSlides === 0) return;

    this.instance = tns({
      container: this.slider,
      items: 3,
      slideBy: 3,
      mouseDrag: true,
      autoplay: false,
      prevButton: this.prevButton,
      nextButton: this.nextButton,
      loop: false,
      nav: !!this.showThumbs,
      controls: !!this.controls,
      autoHeight: true,
      responsive: this.breakpoints,
      navAsThumbnails: !!this.showThumbs,
      navContainer: this.navElement || false,
      onInit: () => this.afterInit(),
      ...this.config,
    });

    if (this.instance) {
      this.updateNavigation();
      this.bindDefaultEvents();
    }

    // Check lazy load event, if image changes heights, reload the slider height
    Events.$on('image::loaded', (event, data) => {
      const updatedImage = data;
      const sliderImageData = this.imagesData.find(imageData => imageData.image === updatedImage);

      // If event image is in slider, and height has changed, reload slider height
      if (sliderImageData && sliderImageData.offsetHeight !== updatedImage.offsetHeight) {
        this.instance.updateSliderHeight();
      }
    });
  }
}

export default Slider;
